import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../config/api';
import Layout from './Layout';
import PlanUsersModal from './PlanUsersModal';
import PlanModal from './PlanModal';
import ConfirmModal from './ConfirmModal';
import { showError, showSuccess } from '../utils/toast';
import './Plans.css';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPlans, setTotalPlans] = useState(0);
  const lastFetchedPage = useRef(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    confirmButtonType: 'danger'
  });

  useEffect(() => {
    // Reset the ref if page changed (allow fetch for new page)
    if (lastFetchedPage.current !== null && lastFetchedPage.current !== currentPage) {
      lastFetchedPage.current = null;
    }

    // Prevent double call in React StrictMode for the same page
    if (lastFetchedPage.current === currentPage) {
      return;
    }

    // Mark this page as fetched
    lastFetchedPage.current = currentPage;

    fetchPlanStats();
  }, [currentPage]);

  const fetchPlanStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }

      // Fetch plans with pagination from API
      const queryParams = `page=${currentPage}&page_size=${pageSize}`;
      const response = await apiClient.get(`/admin/plans?${queryParams}`, token);

      console.log('Plans API Response:', response);

      let plansArray = [];
      let totalPagesCount = 1;
      let totalPlansCount = 0;

      // Handle API response structure
      if (response && response.data) {
        if (Array.isArray(response.data.plans)) {
          plansArray = response.data.plans;
          totalPlansCount = response.data.total || plansArray.length;
          const responsePageSize = response.data.page_size || pageSize;
          totalPagesCount = Math.ceil(totalPlansCount / responsePageSize) || 1;
        } else if (Array.isArray(response.data)) {
          plansArray = response.data;
          totalPlansCount = response.total || plansArray.length;
          totalPagesCount = Math.ceil(totalPlansCount / pageSize) || 1;
        }
      } else if (Array.isArray(response)) {
        plansArray = response;
        totalPlansCount = response.length;
        totalPagesCount = Math.ceil(totalPlansCount / pageSize) || 1;
      } else if (response && response.plans && Array.isArray(response.plans)) {
        plansArray = response.plans;
        totalPlansCount = response.total || plansArray.length;
        totalPagesCount = Math.ceil(totalPlansCount / pageSize) || 1;
      }

      console.log('Extracted plans array:', plansArray);
      console.log('Plans count:', plansArray.length);
      console.log('Total plans:', totalPlansCount);
      console.log('Total pages:', totalPagesCount);

      setPlans(plansArray);
      setTotalPages(totalPagesCount);
      setTotalPlans(totalPlansCount);
    } catch (error) {
      console.error('Error fetching plan stats:', error);
      showError('Failed to fetch plan statistics: ' + (error.message || 'Unknown error'));
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUsers = async (planName) => {
    // Create a plan object for the modal
    const plan = { name: planName };
    setSelectedPlan(plan);
    setShowUsersModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) return 'Free';
    return `$${price}`;
  };

  const handleUsersModalClose = () => {
    setShowUsersModal(false);
    setSelectedPlan(null);
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    setShowPlanModal(true);
  };

  const handleEditPlan = (plan) => {
    console.log('handleEditPlan - Plan object being passed to modal:', plan);
    console.log('Plan pricing_options:', plan.pricing_options);
    setEditingPlan(plan);
    setShowPlanModal(true);
  };

  const handlePlanUpdated = (updatedPlan) => {
    console.log('handlePlanUpdated called with:', updatedPlan);
    console.log('Updated plan pricing_options:', updatedPlan.pricing_options);
    // Update the plan in local state immediately with the updated data
    setPlans(prevPlans => {
      const updated = prevPlans.map(p => {
        if (p.id === updatedPlan.id) {
          // Merge the updated plan data, ensuring pricing_options are preserved
          const merged = {
            ...p,
            ...updatedPlan
          };
          // Explicitly set pricing_options if they exist in updatedPlan
          if (updatedPlan.pricing_options && Array.isArray(updatedPlan.pricing_options)) {
            merged.pricing_options = updatedPlan.pricing_options;
            console.log('Set pricing_options in merged plan:', merged.pricing_options);
          }
          console.log('Updated plan in state:', merged);
          return merged;
        }
        return p;
      });
      console.log('All plans after update:', updated);
      return updated;
    });
  };

  const handleStatusToggle = (plan) => {
    const action = plan.is_active ? 'deactivate' : 'activate';

    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Plan`,
      message: `Are you sure you want to ${action} this plan?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancel',
      confirmButtonType: plan.is_active ? 'danger' : 'primary',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          const token = localStorage.getItem('token');
          // Toggle the status
          const updatedPlan = { ...plan, is_active: !plan.is_active };

          // Use the specific endpoint for updating plan details
          await apiClient.put(`/admin/plans/${plan.id}`, updatedPlan, token);

          // Update local state
          setPlans(prevPlans => prevPlans.map(p =>
            p.id === plan.id ? { ...p, is_active: updatedPlan.is_active } : p
          ));

          showSuccess(`Plan ${action}d successfully`);
        } catch (error) {
          console.error('Error updating plan status:', error);
          showError('Failed to update plan status: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const handleDeletePlan = (planId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Plan',
      message: 'Are you sure you want to delete this plan? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonType: 'danger',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          const token = localStorage.getItem('token');
          await apiClient.delete(`/admin/plans/${planId}`, token);

          // Remove plan from local state
          setPlans(prevPlans => prevPlans.filter(p => p.id !== planId));

          // Update total plans count
          setTotalPlans(prevTotal => {
            const newTotal = Math.max(0, prevTotal - 1);
            setTotalPages(Math.max(1, Math.ceil(newTotal / pageSize)));
            return newTotal;
          });

          // If we deleted the last item on the current page, go to the previous page
          if (plans.length === 1 && currentPage > 1) {
            setCurrentPage(prev => prev - 1);
          }

          showSuccess('Plan deleted successfully');
        } catch (error) {
          console.error('Error deleting plan:', error);
          showError('Failed to delete plan: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const handlePlanModalClose = () => {
    setShowPlanModal(false);
    setEditingPlan(null);
    // Refresh plans after add/edit with a small delay to ensure API update is complete
    setTimeout(() => {
      lastFetchedPage.current = null;
      fetchPlanStats();
    }, 300);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    const halfMax = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    // Adjust start/end if near boundaries
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    if (endPage - startPage + 1 < maxPagesToShow) {
      endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key={1} className="pagination-number" onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-number ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-number"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (loading) {
    return (
      <Layout>
        <div className="plans-loading">
          <div className="loading-spinner">Loading plans...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="plans-page">
        {/* Header Section */}
        <div className="plans-page-header">
          <div className="header-left">
            <h1 className="page-title">Plans</h1>
          </div>
          <div className="filter-tabs-right">
            {/* Filters and Menu buttons hidden per request */}
            <button className="add-plan-btn" onClick={handleAddPlan}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Plan
            </button>
          </div>
        </div>


        {/* Table Container */}
        <div className="plans-table-wrapper">
          <table className="plans-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Plan Name</th>
                <th>Price</th>
                <th>Status</th>
                <th>Date Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-plans">
                    <div className="empty-state">
                      {loading ? 'Loading...' : 'No plans found'}
                    </div>
                  </td>
                </tr>
              ) : (
                plans.map((plan, index) => {
                  const rowNumber = (currentPage - 1) * pageSize + index + 1;
                  const isActive = plan.is_active;

                  // Get price from pricing_options or price_usd
                  let planPricesDisplay = formatPrice(plan.price_usd);

                  if (plan.pricing_options && Array.isArray(plan.pricing_options) && plan.pricing_options.length > 0) {
                    const validOptions = plan.pricing_options.filter(opt => opt.price_usd > 0);
                    if (validOptions.length > 0) {
                      planPricesDisplay = validOptions.map(opt => {
                        let intervalStr = '';
                        if (opt.plan_interval === 'monthly') intervalStr = '/mo';
                        else if (opt.plan_interval === 'yearly') intervalStr = '/yr';
                        else if (opt.plan_interval === 'days') intervalStr = `/${opt.duration_days}d`;
                        else intervalStr = `/${opt.plan_interval}`;
                        return `${formatPrice(opt.price_usd)}${intervalStr}`;
                      }).join(' • ');
                    } else {
                      planPricesDisplay = 'Free';
                    }
                  } else if (plan.price_usd === 0 || plan.price_usd === null) {
                    planPricesDisplay = 'Free';
                  }

                  return (
                    <tr key={plan.id}>
                      <td className="row-number">{rowNumber}</td>
                      <td>
                        <div className="plan-name-cell">
                          <div className="plan-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                          </div>
                          <div className="plan-name-info">
                            <div className="plan-name">{plan.name || 'N/A'}</div>
                            {plan.max_avatars !== undefined && (
                              <div className="plan-details">
                                {plan.max_avatars} avatars • {plan.max_storage_gb || 0}GB • {plan.weekly_tokens_remaining || 0}W/{plan.monthly_tokens_remaining || 0}M tokens
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="plan-price-value">{planPricesDisplay}</span>
                      </td>
                      <td>
                        <div
                          className={`status-indicator ${isActive ? 'positive' : 'negative'}`}
                          onClick={() => handleStatusToggle(plan)}
                          style={{ cursor: 'pointer' }}
                          title={`Click to ${isActive ? 'deactivate' : 'activate'} plan`}
                        >
                          <span className="status-arrow">
                            {isActive ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="18 15 12 9 6 15"></polyline>
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            )}
                          </span>
                          <span className="status-text">{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                        </div>
                      </td>
                      <td className="date-cell">{formatDate(plan.created_at || plan.date_created)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEditPlan(plan)}
                            title="Edit Plan"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeletePlan(plan.id)}
                            title="Delete Plan"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {showUsersModal && selectedPlan && (
          <PlanUsersModal
            plan={selectedPlan}
            onClose={handleUsersModalClose}
          />
        )}

        {showPlanModal && (
          <PlanModal
            plan={editingPlan}
            onClose={handlePlanModalClose}
            onPlanUpdated={handlePlanUpdated}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          confirmButtonType={confirmModal.confirmButtonType}
        />

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="table-pagination">
            <div className="pagination-left">
              <select
                className="page-size-select"
                value={pageSize}
                onChange={(e) => {
                  console.log('Page size changed to:', e.target.value);
                }}
              >
                <option value="10">10 / page</option>
                <option value="15">15 / page</option>
                <option value="20">20 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
              </select>
            </div>

            <div className="pagination-right">
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <div className="pagination-numbers">
                {renderPaginationNumbers()}
              </div>

              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Plans;
